import {
  TASK_STATUS_LABELS,
  zeitfensterStand,
  type PoolKey,
  type TaskSource,
  type TaskStatus,
} from '@netcon-work/shared'
import { useQuery } from '@tanstack/react-query'
import { poolsQuery } from '../lib/queries'
import { AufgabenTitel, useEscSchliesst } from './AufgabeKontext'
import { Chip, POOLS, SourceBadge } from './ui/primitives'
import { useAufgabenDieserWelt } from '../lib/welt'
import { hintergrundSchliesst } from './ui/hintergrund'

/**
 * **Eine Gruppe oder mehrere.** Eine Zelle der Kachel liefert eine Gruppe ohne Überschrift;
 * ein Klick auf den **Namen** liefert alle Stufen dieser Person, jede mit ihrer eigenen
 * (Nils, 20.08.2026: *„den Namen sollte man auch noch klicken können – dass ich gruppiert nach
 * heute, 5 T, 10 T, Monat, Quartal, ohne für die Person die Einträge sehe"*).
 */
export type AufgabenGruppe = { titel?: string; ids: string[] }

/**
 * **Die Aufgaben hinter einer Zahl** (20.08.2026).
 *
 * Nils, in der Abnahme von Phase 2, über die Geplant-Ansicht: *„und nicht klickbar"*. Im
 * Rückblick führt jede Zahl ins Tages-Popup; nach vorn führte keine irgendwohin. Das hier ist
 * die Entsprechung für den Blick nach vorn – für die Geplant-Kachel **und** für die Tabelle
 * „wie viel es ist" darunter.
 *
 * ## Warum die Aufgaben aus dem Client kommen und nicht vom Server
 *
 * Der Bestand liegt ohnehin vollständig im Browser (Leitplanke 2) – die Kachel liefert nur
 * **Kennungen**. Eine zweite Abfrage würde dieselbe Rechnung ein zweites Mal machen, und zwei
 * Rechnungen laufen irgendwann auseinander. So ist die Zahl in der Zelle immer genau die Länge
 * dieser Liste.
 *
 * ## Warum die Frist an jeder Zeile steht
 *
 * Weil „in 5 Tagen" ohne Datum die Frage ist, die Nils schon einmal gestellt hat (Anker,
 * `zeitfenster_ab`). Wo ein Anker fehlt – die Zeilen von vor dieser Spalte – steht nur die
 * Stufe, ohne erfundenes Datum.
 */
export function AufgabenPopup({
  titel,
  untertitel,
  gruppen,
  schliessen,
}: {
  titel: string
  untertitel: string
  gruppen: AufgabenGruppe[]
  schliessen: () => void
}) {
  /** Das Tages-Popup hängt am Leitstand und ist damit gebunden (Z-07, E-235). */
  const { aufgaben: alle } = useAufgabenDieserWelt()
  /**
   * **Die Pools kommen aus `pools.all`, nicht aus dem Aufgabenbestand** (Nils, 20.08.2026:
   * *„kannst du wenn vorhanden die Pools hier noch ergänzen"*).
   *
   * `tasks.all` trägt die Zugehörigkeit nicht: eine Aufgabe kann in mehreren Pools liegen, das
   * wäre eine Liste an jeder der 3.177 Zeilen – und die Prozedur ist seit heute die, die den
   * Erst-Render kostet ([E-176](../../../../docs/ENTSCHEIDUNGEN.md)). Hier lädt sie erst, wenn
   * jemand ein Popup öffnet.
   *
   * **„Wenn vorhanden" ist wörtlich zu nehmen:** `pools.all` lässt Erledigtes aus (ein Pool ist
   * ein Vorrat), und wer eine Aufgabe zieht, verlässt den Pool. Wo keine Marke steht, liegt die
   * Aufgabe also wirklich in keinem – das ist eine Auskunft und keine Lücke.
   */
  const { data: pools = [] } = useQuery(poolsQuery)
  useEscSchliesst(schliessen)

  const poolsJeAufgabe = new Map<string, { id: string; label: string }[]>()
  for (const pool of pools) {
    for (const a of pool.aufgaben) {
      const bisher = poolsJeAufgabe.get(a.id) ?? []
      bisher.push({ id: pool.id, label: pool.label })
      poolsJeAufgabe.set(a.id, bisher)
    }
  }

  const nachId = new Map(alle.map((a) => [a.id, a]))
  const gefuellt = gruppen
    .map((g) => {
      const aufgaben = g.ids
        .map((id) => nachId.get(id))
        .filter((a) => !!a)
        /**
         * **Das Dringendste zuerst.** Vorher stand die Reihenfolge der Kennungen da – also die
         * des Aufrufers, die niemand liest. Sortiert wird nach dem Datum, das die Zeile rechts
         * zeigt: Überfälliges oben, dann das Nächstliegende, Datumsloses ans Ende.
         *
         * Ohne Datum bleibt der Titel als Anker, damit die Liste bei jedem Öffnen gleich
         * aussieht – eine Reihenfolge, die sich zwischen zwei Blicken ändert, kostet mehr, als
         * sie ordnet.
         */
        .sort((x, y) => {
          const wann = (a: (typeof aufgaben)[number]) =>
            zeitfensterStand(a.zeitfenster, a.zeitfensterAb ?? null).datum ??
            (a.dueAt ? new Date(a.dueAt).toLocaleDateString('de-DE') : null)
          const sx = sortierbar(wann(x))
          const sy = sortierbar(wann(y))
          if (sx !== sy) return sx < sy ? -1 : 1
          return x.title.localeCompare(y.title, 'de')
        })

      /**
       * Was in dieser Gruppe **bei allen gleich** ist, gehört in die Überschrift und nicht in
       * jede Zeile.
       *
       * > **Erst mit einer Ausnahme für Einzelgruppen gebaut, und das war falsch.** Der Gedanke
       * > war: bei einer einzigen Aufgabe wäre die Überschrift die Zeile ein zweites Mal. Das
       * > stimmt für sich genommen – erzeugt aber genau den Eindruck, den Nils am 24.08.2026
       * > gemeldet hat (*„das sieht grafisch noch verwirrend aus"*): Gruppen mit vier Aufgaben
       * > sahen anders aus als solche mit einer, weil dort plötzlich „In Arbeit · Esther
       * > Broekhuis" in der Zeile stand. **Uneinheitlichkeit kostet mehr als Wiederholung** –
       * > eine Liste, deren Zeilen unterschiedlich gebaut sind, muss man einzeln lesen.
       */
      const einheitlich = <T,>(f: (a: (typeof aufgaben)[number]) => T): T | null => {
        const erster = aufgaben[0]
        if (!erster) return null
        const wert = f(erster)
        return aufgaben.every((a) => f(a) === wert) ? wert : null
      }
      const status = einheitlich((a) => a.status)
      const bearbeiter = einheitlich((a) => a.assigneeName)

      return {
        titel: g.titel,
        aufgaben,
        gleicherStatus: status !== null,
        gleicherBearbeiter: bearbeiter !== null,
        /** Was oben steht, weil es unten zehnmal dasselbe wäre. */
        gemeinsam: [
          status ? (TASK_STATUS_LABELS[status as TaskStatus] ?? status) : null,
          /**
           * Der Name nur, wenn er **nicht schon** in der Gruppenüberschrift steht – im
           * Tages-Popup heißt die Gruppe „Christian Hunhoff · läuft", da wäre er das dritte Mal.
           */
          bearbeiter && !(g.titel ?? '').includes(bearbeiter) ? bearbeiter : null,
        ].filter((x): x is string => Boolean(x)),
      }
    })
    .filter((g) => g.aufgaben.length > 0)
  const gesamt = gefuellt.reduce((n, g) => n + g.aufgaben.length, 0)

  return (
    <div
      className="fixed inset-x-0 bottom-0 unter-leiste z-40 flex items-start justify-center bg-[rgb(23_22_20/0.32)] p-3 pt-[4vh] sm:p-6 sm:pt-[8vh]"
      {...hintergrundSchliesst(schliessen)}
      role="dialog"
      aria-modal="true"
      aria-label={`Aufgaben: ${titel}`}
    >
      <div className="flex max-h-[min(90vh,100%)] sm:max-h-[min(80vh,100%)] w-full min-w-0 max-w-[720px] flex-col rounded-card border border-line bg-surface shadow-card">
        <div className="flex items-center gap-2 border-b border-line px-[18px] py-3.5">
          <h3 className="text-sm font-bold">{titel}</h3>
          <span className="text-xs text-muted">{untertitel}</span>
          <button
            type="button"
            onClick={schliessen}
            className="ml-auto cursor-pointer rounded-lg px-2 py-1 text-ink2 hover:bg-[#f3f2ef]"
            aria-label="Schließen"
          >
            ✕
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-[18px] py-3.5">
          {/**
           * **Der Bestand kann noch laden.** Dann ist die Liste leer, obwohl die Zahl in der
           * Zelle stand – und „keine Aufgaben" wäre an dieser Stelle eine Falschaussage.
           */}
          {gesamt === 0 && (
            <p className="text-[13px] text-muted">
              {alle.length === 0 ? 'Lädt …' : 'Diese Aufgaben sind nicht mehr im Bestand.'}
            </p>
          )}

          {gefuellt.map((gruppe, i) => (
            /**
             * **Die Linie trennt Gruppen, nicht Zeilen.** Vorher hatte jede Aufgabe einen
             * grauen Strich unter sich – bei fünf Aufgaben fünf Striche, die die Gruppe
             * zerschnitten, statt sie zusammenzuhalten. Jetzt genau eine Linie je Gruppe,
             * und innerhalb trennt Luft.
             */
            <div key={gruppe.titel ?? i} className={i > 0 ? 'mt-4' : ''}>
              {gruppe.titel && (
                /**
                 * **Die Überschrift ist ein Band, keine Zeile** (Nils, 25.08.2026: *„kannst du
                 * die Gruppen (Namen) noch besser trennen optisch – bisschen deutlicher"*).
                 *
                 * Erst war sie fett in 12 px und stand damit so laut da wie die Titel darunter –
                 * zwei Ebenen, die gleich aussehen, sind keine. Dann klein und gesperrt: richtig
                 * gewichtet, aber zu leise, um zu **trennen**. Eine Linie und etwas Luft
                 * markieren einen Schnitt erst, wenn man hinsieht.
                 *
                 * Ein durchgehend hinterlegtes Band sieht man dagegen, ohne es zu lesen – die
                 * Fläche ist der Schnitt. Es reicht über die ganze Breite (`-mx`), weil ein
                 * eingerücktes Band wie eine weitere Zeile aussähe statt wie eine Grenze.
                 *
                 * **Und es bleibt beim Scrollen oben stehen.** Bei zwölf Aufgaben über sechs
                 * Personen ist die Frage nach dem dritten Bildschirmdrittel *„von wem ist das
                 * hier eigentlich"* – ein Name, den man dafür zurückscrollen muss, ist keine
                 * Antwort.
                 */
                <div className="sticky top-0 z-10 -mx-[18px] mb-1.5 flex items-baseline gap-2 border-y border-line bg-page px-[18px] py-[5px]">
                  <b className="text-[11px] font-bold tracking-[0.07em] text-ink uppercase">
                    {gruppe.titel}
                  </b>
                  <span className="text-[11px] text-muted">{gruppe.aufgaben.length}</span>
                  {/**
                   * **Was in der ganzen Gruppe gleich ist, steht einmal hier** statt zehnmal
                   * darunter (Nils, 24.08.2026: *„das sieht noch unübersichtlich aus, da erkenne
                   * ich gar nicht richtig, was Sache ist"*).
                   *
                   * Er hatte recht, und der Grund war Wiederholung: In einer Gruppe „Christian
                   * Hunhoff · läuft" stand in **jeder** Zeile noch einmal „In Arbeit" und
                   * „Christian Hunhoff". Vier Angaben, von denen zwei nichts unterscheiden –
                   * und was nichts unterscheidet, ist kein Merkmal, sondern Grundrauschen.
                   */}
                  {gruppe.gemeinsam.map((g) => (
                    <span key={g} className="text-[11px] text-muted">
                      <Trenner />
                      {g}
                    </span>
                  ))}
                </div>
              )}
              {gruppe.aufgaben.map((a) => {
                const stand = zeitfensterStand(a.zeitfenster, a.zeitfensterAb ?? null)
                return (
                  /**
                   * **Ein Raster statt einer Flex-Zeile** – der eigentliche Grund für den
                   * unruhigen Eindruck.
                   *
                   * Die Kennungen sind verschieden lang (`Work` gegen `NetCon #103687`), und in
                   * einer Flex-Zeile schiebt jede ihren Titel woanders hin. Zehn Titel, die auf
                   * zehn verschiedenen Kanten beginnen, sehen aus wie zehn verschiedene Dinge.
                   * Mit fester erster Spalte beginnen alle Titel **auf einer Linie**, und die
                   * zweite Zeile liegt genau darunter statt eingerückt daneben.
                   *
                   * Keine Trennlinie je Zeile mehr: bei fünf Aufgaben waren das fünf graue
                   * Striche, die die Gruppe zerschnitten haben. Getrennt wird durch Luft, und
                   * das Zusammengehörige bleibt zusammen.
                   */
                  <div
                    key={a.id}
                    className="grid grid-cols-[104px_1fr_auto] items-baseline gap-x-3 rounded-md py-[5px] hover:bg-page"
                  >
                    {/**
                     * **Die Kennung statt des Quellennamens.** „NetCon Ticket" brach über zwei
                     * Zeilen und sagte nichts, was die Farbe nicht schon sagt; `#103687` ist
                     * kürzer *und* die Angabe, mit der man in der Quelle danach sucht.
                     */}
                    <span className="min-w-0 truncate">
                      <SourceBadge source={a.sourceId as TaskSource} label={a.externalKey} />
                    </span>

                    <div className="min-w-0">
                      {/**
                       * **Eine halbe Stufe kleiner als im Aufgabendetail** (Nils, 25.08.2026:
                       * *„ggf. auch die Schrift der Aufgaben etwas kleiner"*).
                       *
                       * 13 px waren die Größe einer Aufgabe, die **allein** dasteht. Hier stehen
                       * zwölf davon untereinander, und dann trägt nicht mehr die Größe die
                       * Lesbarkeit, sondern der Abstand zur Umgebung: 12,5 px halbfett gegen
                       * 10,5 px in Grau ist derselbe Kontrast bei ruhigerem Gesamtbild.
                       */}
                      <AufgabenTitel
                        aufgabeId={a.id}
                        titel={a.title}
                        className="text-[12.5px] font-semibold"
                        kuerzen
                      />
                      {/**
                       * **Die zweite Zeile trägt nur, was diese Aufgabe von ihren Nachbarn
                       * unterscheidet.** Pool und Vorhaben immer – sie sind der Kontext, den man
                       * im Daily zuerst braucht. Status und Bearbeiter **nie**, wenn die Gruppe
                       * sie ohnehin oben nennt.
                       */}
                      {(() => {
                        const pools = poolsJeAufgabe.get(a.id) ?? []
                        const teile = [
                          !gruppe.gleicherStatus
                            ? (TASK_STATUS_LABELS[a.status as TaskStatus] ?? a.status)
                            : null,
                          !gruppe.gleicherBearbeiter ? a.assigneeName : null,
                          a.vorhabenLabel,
                        ].filter(Boolean)
                        if (pools.length === 0 && teile.length === 0) return null
                        return (
                          <div className="mt-[2px] flex flex-wrap items-center gap-x-2 gap-y-1 text-[10.5px] text-muted">
                            {pools.map((p) => (
                              <PoolMarke key={p.id} id={p.id} label={p.label} />
                            ))}
                            {teile.map((t, n) => (
                              <span key={`${t}-${n}`}>
                                {(n > 0 || pools.length > 0) && <Trenner />}
                                {t}
                              </span>
                            ))}
                          </div>
                        )
                      })()}
                    </div>

                    {/**
                     * Die Frist bildet die dritte Spalte – „welche ist zuerst dran" beantwortet
                     * ein Auge, das eine Kante entlangfährt, nicht eines, das zehnmal bis zum
                     * Zeilenende liest.
                     */}
                    {frist(a, stand)}
                  </div>
                )
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

/**
 * **Pool-Marke mit der Beschriftung aus der Datenbank.**
 *
 * `PoolTag` aus den Primitiven trägt die Beschriftung fest im Code (*„Tickets"*, *„Projekte"*),
 * die Datenbank aber sagt *„Tickets & Kleinigkeiten"* und *„Projektaufgaben"* – und sie ist die
 * Quelle: Pools sind konfigurierbar ([F-07](../../../../docs/OFFENE-FRAGEN.md)), Nils kann sie
 * umbenennen. Genau so steht es auch im Kommentar über `POOLS`: *„Nur die Optik. Beschriftung,
 * Symbol und Spielregeln kommen aus der Datenbank."*
 *
 * Übernommen wird von dort deshalb nur die **Farbe** – und wo es keine gibt (der Pool
 * „Rauschen" hat im Mockup keine), steht die Marke grau statt gar nicht.
 */
function PoolMarke({ id, label }: { id: string; label: string }) {
  const stil = id in POOLS ? POOLS[id as PoolKey] : null
  if (stil === null) return <Chip>{label}</Chip>
  return (
    <span
      className={`inline-flex items-center gap-[5px] rounded-full px-[9px] py-[2.5px] text-[10.5px] font-semibold ${stil.stil}`}
    >
      <i className={`size-1.5 rounded-full ${stil.punkt}`} />
      {label}
    </span>
  )
}

/**
 * **Die Frist am rechten Rand** – die eine Angabe, die eine Spalte bilden darf.
 *
 * Sie beantwortet *„welche ist zuerst dran"*, und diese Frage liest ein Auge, das eine Kante
 * entlangfährt. Stünde sie mitten in der Nebenzeile, müsste man jede Zeile bis zum Ende lesen,
 * um zwei Termine zu vergleichen.
 *
 * `dueAt` schlägt das Zeitfenster: ein echter Termin aus der Quelle ist eine Zusage, ein
 * Zeitfenster eine Einstufung. Überfälliges trägt die Sofort-Farbe – die einzige Stelle in
 * dieser Zeile, an der Farbe etwas bedeutet.
 */
function frist(
  aufgabe: { dueAt?: string | Date | null; zeitfenster?: string | null },
  stand: { datum: string | null; abgelaufen: boolean },
) {
  if (aufgabe.dueAt) {
    return (
      <span className="shrink-0 text-[10.5px] font-semibold text-ink2 tabular-nums">
        fällig {new Date(aufgabe.dueAt).toLocaleDateString('de-DE')}
      </span>
    )
  }
  if (stand.datum) {
    return (
      <span
        className={`shrink-0 text-[10.5px] tabular-nums ${
          stand.abgelaufen ? 'font-semibold text-pool-sofort' : 'text-muted'
        }`}
      >
        {stand.abgelaufen ? `überfällig seit ${stand.datum}` : `bis ${stand.datum}`}
      </span>
    )
  }
  /**
   * **Ein Zeitfenster ohne Anker ist keine Frist**, sondern eine Einstufung von vor der Spalte
   * `zeitfenster_ab`. Es bekommt deshalb kein erfundenes Datum, sondern ein Wort.
   */
  if (aufgabe.zeitfenster) {
    return <span className="shrink-0 text-[10.5px] text-muted">ohne Anker</span>
  }
  return <span className="shrink-0 text-[10.5px] text-muted">ohne Termin</span>
}

/**
 * Ein Datum `TT.MM.JJJJ` als sortierbare Zeichenkette. `null` sortiert ans Ende – „irgendwann"
 * ist nie dringender als „am Freitag".
 */
function sortierbar(datum: string | null): string {
  if (!datum) return '9999'
  const treffer = /(\d{1,2})\.(\d{1,2})\.(\d{2,4})/.exec(datum)
  if (!treffer) return '9998'
  const [, t, m, j] = treffer
  const jahr = (j as string).length === 2 ? `20${j}` : (j as string)
  return `${jahr}-${(m as string).padStart(2, '0')}-${(t as string).padStart(2, '0')}`
}

/**
 * **Das Trennzeichen gehört nicht zum Wert.**
 *
 * Stand es als `· {wert}` im selben Textknoten, hieß der Inhalt „· In Arbeit" – und war damit
 * weder als „In Arbeit" zu finden noch von einem Screenreader sinnvoll vorzulesen. Aufgefallen
 * ist es an einem Test, der genau danach suchte; der Vorleser hätte es nie gemeldet.
 */
function Trenner() {
  return (
    <span aria-hidden className="mr-[5px] text-muted">
      ·
    </span>
  )
}
